import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/app/auth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SortArrow } from '@/components/ui/SortArrow';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { cx } from '@/lib/cx';
import { describeAccess, useAccessNames } from '@/lib/flightAccess';
import type { AccessNames } from '@/lib/flightAccess';
import { codeList, unrestrictedAccess } from '@/types/flightAccess';
import type { FlightAccess } from '@/types/flightAccess';
import { fullName, isUserActive, USER_ROLES } from '@/types/user';
import type { CompanyInfo, UserInfo } from '@/types/user';
import type { AccessBinding, AccessCarry } from './FlightAccessSection';
import { roleTone, UserPanel } from './UserPanel';
import type { SaveAction } from './UserPanel';
import { useFlightAccesses } from './useFlightAccesses';
import { useUserParams } from './useUserParams';
import type { UserAccessFilter, UserSortField, UserState } from './useUserParams';
import { useUsers } from './useUsers';
import styles from './UsersPage.module.css';

/**
 * The user list, with one account open in a dialog over it.
 *
 * ADMIN only, and the route says so as well: every call this screen makes answers
 * 403 for any other role, so opening it would be a page of error messages.
 *
 * **Everything here narrows an array, not a query.** `GET /user/all` takes no
 * filter, no sort and no page — it hands back every user in one response. So there
 * is no pager: paging a list that is already in the browser would only make a
 * reader step through pages to find a name that a search box finds instantly, and
 * it would put a page number in the URL that means nothing to the server.
 *
 * There is **no delete**. Blocking is what delete means here — the row stays,
 * because the user's card and everything they have done reference it, and a blocked
 * account cannot sign in. It lives in the panel rather than on the row: it is not
 * something to do by clicking the wrong line of a table.
 *
 * **Flight access** — which flights each account sees — is read beside the users from
 * its own endpoint and joined on the id, as the API guide describes. Only accounts
 * somebody has restricted are in that list; everyone else sees every flight. It is
 * shown as a column and a filter here, and set in the panel.
 */
export const UsersPage = () => {
  const { params, update, select, reset, hasFilters } = useUserParams();
  const { users, isLoading, errorKey, reload, apply } = useUsers();
  const accesses = useFlightAccesses();
  const { user: self, updateUser, signOut } = useAuth();
  const { t } = useTranslation();

  /**
   * What just happened, said once.
   *
   * Page-level rather than inside the panel, because a create switches the panel to
   * the account it just made — which remounts it, and would take any message inside
   * it with it.
   *
   * A warning is the one that stays: "created, but the access was not saved" describes
   * an account that sees every flight right now, and it goes when that is fixed, not
   * when a timer says so.
   */
  const [flash, setFlash] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null);

  useEffect(() => {
    if (flash === null || flash.tone === 'warning') return undefined;

    // Long enough to read, short enough that it is never mistaken for the state of
    // a later edit.
    const timer = window.setTimeout(() => setFlash(null), 6000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  /** A create whose access write failed: its selection, for the panel that opens next. */
  const [carry, setCarry] = useState<{ userId: number; carry: AccessCarry } | null>(null);

  const { byUser, hasLoaded: accessesLoaded } = accesses;

  /**
   * The SAVED access of one account: its entry, or the default when the list has
   * loaded and it has none. `null` — not known, because the list has not arrived or
   * could not be read; then nothing about the account's access may be claimed.
   */
  const storedAccess = useCallback(
    (userId: number): FlightAccess | null =>
      accessesLoaded ? (byUser.get(userId) ?? unrestrictedAccess(userId)) : null,
    [accessesLoaded, byUser],
  );

  /** Names for every airport any access mentions — the column's tooltips read them. */
  const accessAirports = useMemo(
    () => codeList([...byUser.values()].flatMap((access) => access.airports)),
    [byUser],
  );
  const accessNames = useAccessNames(accessAirports);

  /**
   * The companies a user can be created in.
   *
   * Derived from the users themselves: this API has no endpoint that lists
   * companies, and `POST /user/add` requires one as a query parameter. So the
   * choices are the companies that already have at least one user — which covers
   * adding a colleague to an existing company, and does not cover the first user of
   * a brand new one. That needs an endpoint this backend does not expose.
   */
  const companies = useMemo<CompanyInfo[]>(() => {
    const found = new Map<number, CompanyInfo>();

    for (const user of users) {
      if (user.companyInfo) found.set(user.companyInfo.id, user.companyInfo);
    }

    return [...found.values()].sort((left, right) =>
      (left.companyName ?? '').localeCompare(right.companyName ?? ''),
    );
  }, [users]);

  const visible = useMemo(() => {
    const needle = params.query.trim().toLowerCase();

    const filtered = users.filter((user) => {
      if (params.role !== '' && user.role !== params.role) return false;
      if (params.state !== '' && stateOf(user) !== params.state) return false;

      const access = appliedAccess(user, storedAccess(user.id));

      // An access that is not known matches neither choice: claiming an account sees
      // every flight because its access could not be read would be the one wrong
      // answer this filter can give.
      if (params.access !== '' && (access === null || accessFilterOf(access) !== params.access)) {
        return false;
      }

      return needle === '' || matches(user, needle) || matchesAccess(access, needle);
    });

    // Sorted on a copy: `users` belongs to the hook, and sorting it in place would
    // reorder the list the panel is reading from mid-render.
    return [...filtered].sort((left, right) => {
      const order = compare(left, right, params.sort);
      return params.dir === 'ascending' ? order : -order;
    });
  }, [
    users,
    params.query,
    params.role,
    params.state,
    params.access,
    params.sort,
    params.dir,
    storedAccess,
  ]);

  // Read out once: narrowing a union through `params.selection` is lost the moment
  // it crosses into a callback, and the id is needed inside one.
  const { selection } = params;

  const selected =
    selection.mode === 'edit' ? (users.find((user) => user.id === selection.id) ?? null) : null;

  // Asked for an account that is not in the list at all — a link to a user id that
  // no longer exists. Looked up in the WHOLE list rather than the filtered one, so a
  // filter hiding a row never reads as the row being gone, and the panel stays open
  // over an account the reader has just blocked out of view.
  const missing = selection.mode === 'edit' && selected === null && !isLoading && !errorKey;

  // Creating and editing open the same dialog; only editing has an account to show.
  const isCreating = selection.mode === 'create';

  const handleSaved = (saved: UserInfo, action: SaveAction, accessCarry?: AccessCarry) => {
    apply(saved);

    // An administrator editing their OWN row is the one case where a write from this
    // screen also changes the signed-in user, and the header would otherwise keep
    // their old name until they signed in again. Guarded by the id: putting anyone
    // else's account into the session would leave every role check reading from the
    // wrong user.
    if (self?.id === saved.id) {
      // Ending your own sessions — directly, or by setting yourself a new password —
      // killed the token this screen is using. Signed out now rather than at the
      // next request, so the screen does not pretend to be signed in.
      if (action === 'sessionsEnded' || action === 'savedAndSignedOut') {
        signOut();
        return;
      }
      updateUser(saved);
    }

    const name = saved.username;
    setFlash({
      text: t(`users.flash.${action}`, { name }),
      tone: action === 'createdWithoutAccess' ? 'warning' : 'success',
    });

    // Kept until the access is saved, not until the next save of anything: an edit to
    // the account's name does not make its access any less unsaved.
    if (accessCarry) setCarry({ userId: saved.id, carry: accessCarry });

    // A save is done once it is written, so the dialog closes and the message above
    // the table is what confirms it — inside a dialog it would sit behind the scrim.
    // Two cases keep a dialog open: blocking, whose result is the badge and button in
    // the dialog itself, and a create whose access write failed — that opens the new
    // account with the access still to save, because it sees every flight until it is.
    if (action === 'created' || action === 'saved') {
      select({ mode: 'closed' });
    } else if (action === 'createdWithoutAccess') {
      select({ mode: 'edit', id: saved.id });
    }
  };

  const handleAccessSaved = (saved: FlightAccess) => {
    accesses.apply(saved);

    // The warning was about this account seeing every flight until its access was
    // saved. It has been, so the warning is no longer true.
    if (carry?.userId === saved.userId) {
      setCarry(null);
      setFlash(null);
    }
  };

  /** The panel's view of one account's access — see `AccessBinding`. */
  const accessBinding = (userId: number | null): AccessBinding => ({
    access: userId === null ? null : storedAccess(userId),
    isLoading: accesses.isLoading,
    hasFailed: accesses.hasFailed,
    reload: accesses.reload,
    onSaved: handleAccessSaved,
    carry: carry !== null && carry.userId === userId ? carry.carry : null,
  });

  const refresh = () => {
    reload();
    accesses.reload();
  };

  return (
    <>
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <h1 className={styles.title}>{t('users.title')}</h1>

          <span className={styles.count}>
            {t('users.found', { count: visible.length })}
            {visible.length !== users.length && (
              <span className={styles.countTotal}>{t('users.ofTotal', { total: users.length })}</span>
            )}
          </span>

          <div className={styles.spacer} />

          <Button size="sm" onClick={refresh} disabled={isLoading || accesses.isLoading}>
            {t('common.refresh')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => select({ mode: 'create' })}
            disabled={selection.mode === 'create'}
          >
            {t('users.add')}
          </Button>
        </div>

        <div className={styles.filters}>
          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('common.search')}</span>
            {/* Controlled and undebounced, unlike the board's column filters: a
                keystroke here narrows an array that is already in the browser, so
                there is no request to spare. That also means Back and "Clear all"
                reach it without the effect the debounced version needs. */}
            <input
              type="search"
              className={cx(styles.search, params.query !== '' && styles.searchActive)}
              value={params.query}
              onChange={(event) => update({ query: event.target.value })}
              placeholder={t('users.searchPlaceholder')}
              aria-label={t('users.searchPlaceholder')}
              spellCheck={false}
              autoComplete="off"
            />
          </label>

          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('auth.role')}</span>
            <Select
              options={USER_ROLES.map((role) => ({ value: role, label: t(`users.roles.${role}`) }))}
              placeholder={t('users.allRoles')}
              value={params.role}
              active={params.role !== ''}
              onChange={(event) => update({ role: event.target.value as typeof params.role })}
              aria-label={t('auth.role')}
            />
          </label>

          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('users.columns.status')}</span>
            <Select
              options={[
                { value: 'active', label: t('users.states.active') },
                { value: 'blocked', label: t('users.states.blocked') },
              ]}
              placeholder={t('users.allStates')}
              value={params.state}
              active={params.state !== ''}
              onChange={(event) => update({ state: event.target.value as UserState | '' })}
              aria-label={t('users.columns.status')}
            />
          </label>

          <label className={styles.filter}>
            <span className={styles.filterLabel}>{t('users.columns.access')}</span>
            <Select
              options={[
                { value: 'all', label: t('users.accessStates.all') },
                { value: 'restricted', label: t('users.accessStates.restricted') },
              ]}
              placeholder={t('users.allAccess')}
              value={params.access}
              active={params.access !== ''}
              onChange={(event) => update({ access: event.target.value as UserAccessFilter | '' })}
              aria-label={t('users.columns.access')}
            />
          </label>

          <Button size="sm" onClick={reset} disabled={!hasFilters}>
            {t('users.resetFilters')}
          </Button>
        </div>

        {/* Announced when it appears, because the reader's focus is usually still in
            the panel that caused it. */}
        {flash && (
          <p className={cx(styles.flash, flash.tone === 'warning' && styles.flashWarning)} role="status">
            {flash.text}
          </p>
        )}

        {errorKey && (
          <p className={styles.error} role="alert">
            {t(errorKey)}
          </p>
        )}

        {/* Only once the users themselves loaded: without them there is no column to
            explain. */}
        {accesses.hasFailed && !errorKey && (
          <p className={styles.warning}>{t('users.access.listFailed')}</p>
        )}

        {missing && <p className={styles.warning}>{t('users.notFound')}</p>}

        <div className={styles.tableWrap}>
          {(isLoading || accesses.isLoading) && <div className={styles.loadingBar} aria-hidden="true" />}

          <table className={cx(styles.table, isLoading && styles.loading)}>
            <thead>
              <tr className={styles.headRow}>
                {COLUMNS.map((column) => (
                  <SortHead
                    key={column.labelKey}
                    field={column.field}
                    labelKey={column.labelKey}
                    sort={params.sort}
                    dir={params.dir}
                    onSort={(field) =>
                      update({
                        sort: field,
                        dir:
                          params.sort === field && params.dir === 'ascending'
                            ? 'descending'
                            : 'ascending',
                      })
                    }
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {visible.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.message}>
                    {t(users.length === 0 ? 'common.noData' : 'users.nothingFound')}
                  </td>
                </tr>
              ) : (
                visible.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    access={storedAccess(user.id)}
                    accessNames={accessNames}
                    isSelf={self?.id === user.id}
                    isOpen={selected?.id === user.id}
                    onOpen={() => select({ mode: 'edit', id: user.id })}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Outside the page, because it is a dialog in the browser's top layer: the
          table never has to make room for it. */}
      {(isCreating || selected !== null) && (
        <UserPanel
          // Keyed by the account, so choosing another one — or the one a create has
          // just made — rebuilds the form from it instead of an effect copying it over
          // what is on screen.
          key={selected === null ? 'create' : String(selected.id)}
          user={selected}
          companies={companies}
          defaultCompanyId={self?.companyInfo?.id ?? null}
          selfId={self?.id ?? null}
          flightAccess={accessBinding(selected?.id ?? null)}
          onSaved={handleSaved}
          onClose={() => select({ mode: 'closed' })}
        />
      )}
    </>
  );
};

/**
 * The columns, in order.
 *
 * A list rather than repeated markup: the head row and the "nothing found" colspan
 * have to agree on the count, and a list is the only version of that which cannot
 * drift.
 */
const COLUMNS: ReadonlyArray<{ field: UserSortField | null; labelKey: Key }> = [
  { field: 'username', labelKey: 'users.columns.username' },
  { field: 'name', labelKey: 'users.columns.name' },
  { field: 'role', labelKey: 'users.columns.role' },
  // Beside the role, because the two together are what this account may see. No sort:
  // ordering by a list of codes orders by whichever code happens to come first; the
  // filter is how a reader gathers the restricted accounts.
  { field: null, labelKey: 'users.columns.access' },
  // No company column: the account's company is on the panel, and the search box
  // still matches company names, so the table spends the width on what is read.
  // No sort of its own: the cell holds two values, so ordering by it would order by
  // whichever of them happened to be first.
  { field: null, labelKey: 'users.columns.contact' },
  // Sorting two values into two blocks is what the status filter already does, and
  // it says which block you are looking at.
  { field: null, labelKey: 'users.columns.status' },
  { field: 'created', labelKey: 'users.columns.created' },
];

/** Which of a user's fields the search box looks at. */
const matches = (user: UserInfo, needle: string): boolean =>
  [
    user.username,
    user.firstName,
    user.middleName,
    user.lastName,
    user.userCard?.email,
    user.userCard?.phoneNumber,
    user.companyInfo?.companyName,
  ].some((field) => typeof field === 'string' && field.toLowerCase().includes(needle));

const stateOf = (user: UserInfo): UserState => (isUserActive(user) ? 'active' : 'blocked');

/**
 * The access that APPLIES to an account, from the one that is stored.
 *
 * An administrator always sees every flight: a restriction stored for one is kept by
 * the server but ignored while the role is ADMIN. So the filter and the search read
 * this, and only the cell — which can say both things — reads the stored one.
 */
const appliedAccess = (user: UserInfo, stored: FlightAccess | null): FlightAccess | null =>
  stored !== null && user.role === 'ADMIN' && stored.restricted ? unrestrictedAccess(user.id) : stored;

const accessFilterOf = (access: FlightAccess): UserAccessFilter =>
  access.restricted ? 'restricted' : 'all';

/** A restricted account is found by its codes too: `nqz` lists everyone limited to Astana. */
const matchesAccess = (access: FlightAccess | null, needle: string): boolean =>
  access !== null &&
  access.restricted &&
  [...access.airports, ...access.airlines].some((code) => code.toLowerCase().includes(needle));

/**
 * Orders two users by one column.
 *
 * An empty value sorts LAST in both directions rather than being treated as a value
 * below every other: a list ordered by surname should not open with every account
 * that has no surname filled in.
 */
const compare = (left: UserInfo, right: UserInfo, field: UserSortField): number => {
  const [a, b] = [valueOf(left, field), valueOf(right, field)];

  if (a === '' || b === '') return a === b ? 0 : a === '' ? 1 : -1;
  if (field === 'created') return timeOf(a) - timeOf(b);

  return a.localeCompare(b);
};

const valueOf = (user: UserInfo, field: UserSortField): string => {
  switch (field) {
    case 'username':
      return user.username;
    case 'name':
      return fullName(user);
    case 'role':
      return user.role ?? '';
    case 'created':
      return user.created ?? '';
  }
};

/**
 * A stamp as a number.
 *
 * These come back as `yyyy-MM-dd HH:mm:ss` here rather than the ISO-with-offset the
 * schedule endpoints return, and the two do not sort against each other as text —
 * so they are compared as instants. An unparseable one sorts to the far past, which
 * is where a row with no usable date belongs in a list ordered by date.
 */
const timeOf = (value: string): number => {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * A sortable column head.
 *
 * At module level, not inside UsersPage: a component created during a render is a
 * new type every time, so React unmounts and remounts its whole subtree — which in a
 * table means the search field below loses focus mid-word.
 */
const SortHead = ({
  field,
  labelKey,
  sort,
  dir,
  onSort,
}: {
  /** `null` for a column that does not order the table — it renders as plain text. */
  field: UserSortField | null;
  labelKey: Key;
  sort: UserSortField;
  dir: 'ascending' | 'descending';
  onSort: (field: UserSortField) => void;
}) => {
  const { t } = useTranslation();

  if (field === null) return <th>{t(labelKey)}</th>;

  const sorted = sort === field;

  return (
    <th aria-sort={sorted ? dir : 'none'}>
      <button
        type="button"
        className={cx(styles.sortButton, sorted && styles.sorted)}
        onClick={() => onSort(field)}
        title={t('users.sortHint')}
      >
        {t(labelKey)}
        <SortArrow
          sorted={sorted}
          dir={dir}
          className={styles.sortArrow}
          idleClassName={styles.sortIdle}
        />
      </button>
    </th>
  );
};

/** One account. */
const UserRow = ({
  user,
  access,
  accessNames,
  isSelf,
  isOpen,
  onOpen,
}: {
  user: UserInfo;
  /** The STORED access, or `null` when it is not known. */
  access: FlightAccess | null;
  accessNames: AccessNames;
  isSelf: boolean;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const { t, formatDate } = useTranslation();

  const active = isUserActive(user);
  const name = fullName(user);

  return (
    /*
      The whole row opens the account — a row is one person, and making an
      administrator aim at the login was a target for no reason. Same as the flight
      board and the monitor.

      The button stays: it is what a keyboard reaches and what a screen reader
      announces, neither of which a click handler on a `<tr>` provides. Its own click
      bubbles to this one and both call the same thing, which is idempotent.
    */
    <tr
      className={cx(styles.row, isOpen && styles.selected, !active && styles.blockedRow)}
      onClick={onOpen}
    >
      <td>
        {/* A button, not a link: the account opens in a dialog over the table rather
            than navigating away, so closing it leaves the reader where they were in
            the list. */}
        <button type="button" className={styles.loginButton} onClick={onOpen} aria-haspopup="dialog">
          {user.username}
        </button>
        {isSelf && <span className={styles.selfTag}>{t('users.panel.you')}</span>}
      </td>

      <td>{name || <Empty />}</td>

      <td>
        {user.role ? (
          <Badge tone={roleTone(user.role)}>{t(`users.roles.${user.role}`)}</Badge>
        ) : (
          // No role is not a blank: every request from this account answers 403,
          // which is worth seeing in a list rather than reading as "not filled in".
          <Badge tone="warning">{t('auth.noRole')}</Badge>
        )}
      </td>

      <td>
        <AccessCell user={user} access={access} names={accessNames} />
      </td>

      {/* The column of lines is a span inside the cell, not the cell itself: a `<td>`
          made a flex box stops being a table cell, and stops stretching to the row —
          its border then sits short of the others whenever a neighbour is taller. */}
      <td>
        <span className={styles.contact}>
          {user.userCard?.email && <span className={styles.contactLine}>{user.userCard.email}</span>}
          {user.userCard?.phoneNumber && (
            <span className={cx(styles.contactLine, styles.contactMuted)}>
              {user.userCard.phoneNumber}
            </span>
          )}
          {!user.userCard?.email && !user.userCard?.phoneNumber && <Empty />}
        </span>
      </td>

      <td>
        <Badge tone={active ? 'success' : 'danger'}>
          {t(active ? 'users.states.active' : 'users.states.blocked')}
        </Badge>
      </td>

      <td className={styles.date}>{user.created ? formatDate(user.created) : <Empty />}</td>
    </tr>
  );
};

/**
 * Which flights the account sees, at a glance.
 *
 * "All flights" is the common case and is said quietly. A restriction is the thing
 * worth spotting in a list, so it shows its codes — airports and airlines on their own
 * lines, labelled, because `NQZ` and `KC` are different kinds of condition and a reader
 * should not have to know which is which by length. The sentence is on hover.
 */
const AccessCell = ({
  user,
  access,
  names,
}: {
  user: UserInfo;
  access: FlightAccess | null;
  names: AccessNames;
}) => {
  const { t } = useTranslation();

  if (access === null) return <Empty />;

  if (user.role === 'ADMIN' || !access.restricted) {
    return (
      <span className={styles.accessAll}>
        {t('users.accessStates.all')}
        {/* A stored restriction an administrator does not obey — still worth seeing,
            because it applies again the moment the role is taken away. */}
        {user.role === 'ADMIN' && access.restricted && (
          <span className={styles.accessNote}>{t('users.access.adminIgnored')}</span>
        )}
      </span>
    );
  }

  return (
    <span className={styles.access} title={describeAccess(access, t, names)}>
      {access.airports.length > 0 && (
        <span className={styles.accessLine}>
          <span className={styles.accessKey}>{t('users.access.airports')}</span>
          <span className={styles.accessCodes}>{access.airports.join(' · ')}</span>
        </span>
      )}
      {access.airlines.length > 0 && (
        <span className={styles.accessLine}>
          <span className={styles.accessKey}>{t('users.access.airlines')}</span>
          <span className={styles.accessCodes}>{access.airlines.join(' · ')}</span>
        </span>
      )}
    </span>
  );
};

const Empty = () => <span className={styles.empty}>—</span>;
